import React from 'react';
import { SceneMotes } from './SectorScenes';
import { ramp } from './palette';
import { CHAPTERS } from './storyData';

/* ─── SECTOR WORLD ────────────────────────────────────────────────────────
   The background — and the whole point of it is that it is a PLACE.

   Tinting the sky green does not make a section about healthcare. What makes
   it about healthcare is a HOSPITAL standing in it. Every sector already has
   an authored visual of where it is — `sectorServicesData.js` carries them as
   `heroImg`, and they are the hospital, the grid, the plant and the campus.
   Those are the background.

   ── the structure, back to front ──

     sky      one vertical gradient across the whole frame: near-white at the
              top where the header sits, the sector's colour by the horizon.
              Four stops, all driven per sector.
     glow     the light source, on the horizon behind the subject. This is the
              reference's sun, and it drifts across the section.
     PLACE    the sector itself. SHARP, large, and plainly readable: you are
              meant to see that this is a hospital. It is dimmed a little and
              drifts slower than anything else, and that is all that separates
              it from the foreground.

              An earlier pass blurred it hard to keep it "back". It stopped
              being a hospital and became grey smudges, which is worse than
              having no background at all — the whole reason it is here is to
              say where you are.
     haze     air along the horizon, so sky and floor never meet at a drawn
              edge. It also crosses the place's feet, which is what sits it
              INTO the scene rather than on top of it.
     floor    the ground plane.
     motes    what is in this sector's air.

   ── why it is drawn at stage pixel size ──

   The horizon has to land exactly on the floor line the service renders stand
   on, and that line lives in the trace canvas's coordinate space, inset inside
   the stage. A `slice`-fitted viewBox scales to cover, so the horizon would
   drift as the window changed and the renders would stand in mid-air. This is
   handed the stage's real pixel size and the horizon's real pixel y, and draws
   1:1.
   ───────────────────────────────────────────────────────────────────────── */

export function WorldBack({ width, height, horizon }) {
  const W = Math.max(320, Math.round(width || 1200));
  const H = Math.max(240, Math.round(height || 800));
  const Y = Math.round(horizon || H * 0.72);
  const R = ramp(CHAPTERS[0] ? CHAPTERS[0].accent : '#003CFF');

  return (
    <div className="zx-world" aria-hidden="true">
      {/* ── behind the place ── */}
      <svg
        className="zx-world__svg"
        width={W} height={H} viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none" focusable="false"
      >
        <defs>
          {/* The sky. Every stop is tweened per sector — this is the reference
              taking its scene from dawn to dusk. */}
          <linearGradient id="zx-sky" x1="0" y1="0" x2="0" y2="1">
            <stop data-sky-stop="0" offset="0" stopColor={R.sky0} />
            <stop data-sky-stop="1" offset="0.42" stopColor={R.sky1} />
            <stop data-sky-stop="2" offset="0.76" stopColor={R.sky2} />
            <stop data-sky-stop="3" offset="1" stopColor={R.sky3} />
          </linearGradient>
          <radialGradient id="zx-glow">
            <stop data-glow-stop="0" offset="0" stopColor={R.glow0} stopOpacity="0.40" />
            <stop data-glow-stop="1" offset="55%" stopColor={R.glow1} stopOpacity="0.12" />
            <stop offset="100%" stopColor={R.glow1} stopOpacity="0" />
          </radialGradient>
        </defs>

        <rect x="0" y="0" width={W} height={Y + 2} fill="url(#zx-sky)" />
        <ellipse data-glow cx={W * 0.52} cy={Y} rx={W * 0.46} ry={H * 0.42}
          fill="url(#zx-glow)" />
      </svg>

      {/* ── in front of the place ── */}
      <svg
        className="zx-world__svg"
        width={W} height={H} viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none" focusable="false"
      >
        <defs>
          <linearGradient id="zx-ground" x1="0" y1="0" x2="0" y2="1">
            <stop data-ground-stop="0" offset="0" stopColor={R.ground0} />
            <stop data-ground-stop="1" offset="1" stopColor={R.ground1} />
          </linearGradient>
          <linearGradient id="zx-haze" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#fff" stopOpacity="0" />
            <stop offset="0.5" stopColor="#fff" stopOpacity="0.34" />
            <stop offset="1" stopColor="#fff" stopOpacity="0" />
          </linearGradient>
        </defs>

        <rect x="0" y={Y - 40} width={W} height="80" fill="url(#zx-haze)" />
        <rect x="0" y={Y} width={W} height={H - Y} fill="url(#zx-ground)" />

        {/* Two nested groups: `data-drift` is the slow travel down the whole
            section on its own lagging scrub, `data-surge` is the whip at each
            sector hand-off, which goes out and comes back. Two transforms
            cannot live on one element, and a surge that did not return would
            have to jump back to zero at the next hand-off. */}
        <g data-drift="motes">
          <g className="zx-world__motes" data-surge="motes">
            {CHAPTERS.map(ch => (
              <g key={ch.id} data-scene={ch.id} data-layer="motes">
                <SceneMotes id={ch.id} />
              </g>
            ))}
          </g>
        </g>
      </svg>
    </div>
  );
}
